'use client'
import { ArrowUpCircle, Sparkles } from 'lucide-react'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import Markdown from 'react-markdown'
import TextareaAutosize from 'react-textarea-autosize'

import { Editor } from '@tiptap/react'

import { experimental_useObject as useObject } from '@ai-sdk/react'
import i18next from 'i18next'
import { Command, Group } from '../../../extensions/AICommand/types.js'
import { TiptapSchema } from '../../../lib/tiptapSchema.js'
import { getPrevText } from '../../../lib/utils/index.js'
import AISelectorCommands from '../../menus/TextMenu/components/ai-selector-commands.js'
import CrazySpinner from '../../ui/crazy-spinner.js'
import { DropdownButton } from '../../ui/Dropdown/Dropdown.js'
import { Icon } from '../../ui/Icon.js'
import { ScrollArea } from '../../ui/scroll-area.js'
import { Surface } from '../../ui/Surface.js'
import { Toolbar } from '../../ui/Toolbar.js'
import { useExtractMarkdown } from '../AIEditorPanel/markdown.hook.js'

export type AICommandPanelProps = {
  editor: Editor
  userPrompt?: string
  onOpenChange: (value: boolean) => void
  items: Group[]
}

export const AICommandPanel = forwardRef(
  ({ editor, onOpenChange, userPrompt, items, ...restProps }: AICommandPanelProps, ref) => {
    const [inputValue, setInputValue] = useState(userPrompt ?? '')
    const { view } = editor
    const scrollContainer = useRef<HTMLDivElement>(null)
    const activeItem = useRef<HTMLButtonElement>(null)
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(0)
    const [selectedCommandIndex, setSelectedCommandIndex] = useState(0)

    const editorNode = view.dom as HTMLElement
    const boundigClient = editorNode.getBoundingClientRect()
    const inputRef = useRef<HTMLTextAreaElement>(null)

    const { object, submit, isLoading } = useObject({
      api: '/api/generate',
      schema: TiptapSchema,
    })

    const { markdown } = useExtractMarkdown(object)

    const hasCompletion = Boolean(object)

    const handleClick = useCallback(() => {
      if (object) {
        submit({
          prompt: '',
          body: {
            option: 'zap',
            command: inputValue,
            language: editor?.storage?.aiCommand?.language,
            selection: object,
          },
        })
        setInputValue('')
        return
      }

      submit({
        prompt: '',
        body: {
          option: 'zap',
          command: inputValue,
          language: editor?.storage?.aiCommand?.language,
        },
      })
      setInputValue('')
    }, [editor?.storage?.aiCommand?.language, inputValue, object, submit])

    useEffect(() => {
      if (userPrompt) setInputValue(userPrompt)
    }, [userPrompt])
    useEffect(() => {
      if (!!hasCompletion) {
        editor.storage.aiCommand.completion = object?.content ?? []
      }
    }, [markdown, isLoading])

    useEffect(() => {
      setSelectedGroupIndex(0)
      setSelectedCommandIndex(0)
    }, [items])

    const selectItem = useCallback(
      (groupIndex: number, commandIndex: number) => {
        const command = items[groupIndex].commands[commandIndex]
        setInputValue(command.action(editor))
      },
      [items, editor],
    )

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
        if (event.key === 'ArrowDown') {
          if (!items.length) {
            return false
          }

          const commands = items[selectedGroupIndex].commands

          let newCommandIndex = selectedCommandIndex + 1
          let newGroupIndex = selectedGroupIndex

          if (commands.length - 1 < newCommandIndex) {
            newCommandIndex = 0
            newGroupIndex = selectedGroupIndex + 1
          }

          if (items.length - 1 < newGroupIndex) {
            newGroupIndex = 0
          }

          setSelectedCommandIndex(newCommandIndex)
          setSelectedGroupIndex(newGroupIndex)

          return true
        }

        if (event.key === 'ArrowUp') {
          if (!items.length) {
            return false
          }

          let newCommandIndex = selectedCommandIndex - 1
          let newGroupIndex = selectedGroupIndex

          if (newCommandIndex < 0) {
            newGroupIndex = selectedGroupIndex - 1
            newCommandIndex = items[newGroupIndex]?.commands.length - 1 || 0
          }

          if (newGroupIndex < 0) {
            newGroupIndex = items.length - 1
            newCommandIndex = items[newGroupIndex].commands.length - 1
          }

          setSelectedCommandIndex(newCommandIndex)
          setSelectedGroupIndex(newGroupIndex)

          return true
        }

        if (event.key === 'Enter') {
          if (!items.length || selectedGroupIndex === -1 || selectedCommandIndex === -1) {
            return false
          }

          selectItem(selectedGroupIndex, selectedCommandIndex)

          return true
        }

        return false
      },
    }))

    useEffect(() => {
      if (activeItem.current && scrollContainer.current) {
        const offsetTop = activeItem.current.offsetTop
        const offsetHeight = activeItem.current.offsetHeight

        scrollContainer.current.scrollTop = offsetTop - offsetHeight
      }
    }, [selectedCommandIndex, selectedGroupIndex])

    const createCommandClickHandler = useCallback(
      (groupIndex: number, commandIndex: number) => {
        return () => {
          selectItem(groupIndex, commandIndex)
        }
      },
      [selectItem],
    )

    return (
      <>
        <Surface
          className={`p-2 min-w-[20rem] flex  flex-col gap-2 w-full ${
            hasCompletion ? '-mt-72' : ''
          }`}
          style={{
            width: boundigClient?.width,
          }}
        >
          <>
            {markdown && (
              <div className='flex  w-full'>
                <ScrollArea className='prose p-2 px-4 prose-sm max-h-96' dir={i18next.dir()}>
                  <Markdown>{markdown}</Markdown>
                </ScrollArea>
              </div>
            )}

            {isLoading && (
              <div
                className='flex h-12 w-full items-center px-4 text-sm font-medium text-muted-foreground text-blue-500'
                style={{
                  width: boundigClient?.width,
                }}
              >
                <Sparkles className='mr-2 h-4 w-4 shrink-0  ' />
                AI (Beta) is thinking
                <div className='ml-2 mt-1'>
                  <CrazySpinner />
                </div>
              </div>
            )}

            {!isLoading && (
              <>
                <div className='flex justify-between items-center '>
                  <Sparkles className='mr-2 h-4 w-4 shrink-0 text-blue-500 ' />
                  <TextareaAutosize
                    ref={inputRef}
                    style={{ resize: 'none' }}
                    className='w-full p-2 text-black bg-white  rounded dark:bg-black dark:text-white focus:outline-none outline-none border-0 '
                    value={inputValue}
                    onChange={(e) => {
                      editor.storage.aiCommand.userPrompt = e.target.value
                      setInputValue(e.target.value)
                    }}
                    placeholder={
                      hasCompletion
                        ? i18next.t('tellAiPlaceholder') || 'Tell AI (Beta) what to do next'
                        : i18next.t('askAiPlaceholder') || 'Ask AI (Beta) to edit or generate...'
                    }
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleClick()
                    }}
                  />
                  <Toolbar.Button onClick={handleClick}>
                    <ArrowUpCircle />
                  </Toolbar.Button>
                </div>
              </>
            )}
          </>
        </Surface>
        {!isLoading && (
          <Surface
            ref={scrollContainer}
            className='text-black max-h-[min(80vh,24rem)] overflow-auto flex-wrap mb-8 p-2'
          >
            <div className='grid grid-cols-1 gap-0.5'>
              {hasCompletion ? (
                <AISelectorCommands
                  editor={editor}
                  messages={object}
                  onSelect={(value, options) => {
                    if (options?.option === 'translate') {
                      submit({ prompt: value, body: options })
                    } else {
                      submit({
                        prompt: value,
                        body: { ...options, source: editor?.storage?.aiCommand?.language },
                      })
                    }
                  }}
                />
              ) : (
                items.map((group, groupIndex: number) => (
                  <React.Fragment key={`${group.title}-wrapper`}>
                    <div
                      className='text-neutral-500 text-[0.65rem] col-[1/-1] mx-2 mt-4 font-semibold tracking-wider select-none uppercase first:mt-0.5'
                      key={`${group.title}`}
                    >
                      {group.title}
                    </div>
                    {group.commands.map((command: Command, commandIndex: number) => (
                      <DropdownButton
                        key={`${command.label}`}
                        isActive={
                          selectedGroupIndex === groupIndex && selectedCommandIndex === commandIndex
                        }
                        onClick={createCommandClickHandler(groupIndex, commandIndex)}
                      >
                        <Icon icon={command.icon} className='mr-1' />
                        {command.label}
                      </DropdownButton>
                    ))}
                  </React.Fragment>
                ))
              )}
            </div>
          </Surface>
        )}
      </>
    )
  },
)
